import React from 'react';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { Card } from '../../ui/Card';
import { RADIUS } from '../../../config/designTokens';
import { getAchievementIllustration } from '../../../mascot';
import { RARITY_CONFIG } from '../../../mascot/achievements/achievementData';
import { GoldenShimmer, LegendaryGlow } from './effects';
import { useAchievementShimmer } from './useAchievementShimmer';
import { AchievementProgressBar } from './AchievementProgressBar';
import type { AchievementCardProps } from './types';
export function CollectibleAchievementCard({
  achievement,
  isUnlocked,
  index,
  compact = false
}: AchievementCardProps & {
  compact?: boolean;
}) {
  const isNewlySeen = useAchievementShimmer(achievement.id, isUnlocked);
  const config = RARITY_CONFIG[achievement.rarity];
  const isHighlightRarity = achievement.rarity === 'rare' || achievement.rarity === 'legendary';
  const Illustration = getAchievementIllustration(achievement.id);
  if (!Illustration) return null;
  if (compact) {
    return <motion.div initial={{
      opacity: 0,
      x: -20
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      delay: index * 0.1
    }} className={`relative flex items-center gap-3 p-2 ${RADIUS.card} border-2 transition-all overflow-hidden ${isUnlocked ? `${config.cardBg} ${config.cardBorder} shadow-sm` : 'bg-slate-50 border-slate-200 opacity-80'} ${isNewlySeen ? 'animate-shine' : ''}`} style={isNewlySeen ? {
      animationIterationCount: 1
    } : undefined} data-id="element-1775" data-mp="1ldz4f4imotwl">
        {isUnlocked && achievement.rarity === 'legendary' && <LegendaryGlow data-id="element-1776" data-mp="16hhumhq37nc5" />}
        {isUnlocked && achievement.rarity === 'rare' && <GoldenShimmer data-id="element-1777" data-mp="2mw15uqk2v34g" />}

        <div className="flex-shrink-0 w-16 h-16 rounded-xl overflow-hidden shadow-inner bg-white/50 relative z-10" data-id="element-1778" data-mp="2mv81fw8lcpph">
          <Illustration achievementId={achievement.id} isUnlocked={isUnlocked} rarity={achievement.rarity} variant={achievement.djayVariantUnlocked} size="sm" className="w-full h-full" data-id="element-1779" data-mp="2i0c4chohzc5i" />
        </div>

        <div className="flex-1 min-w-0 relative z-10 py-1" data-id="element-1780" data-mp="1om1g9dyhgdey">
          <div className="flex items-center justify-between gap-2 mb-1" data-id="element-1781" data-mp="1tj9x9b7wabem">
            <h4 className={`font-bold text-sm truncate ${isUnlocked ? 'text-slate-800' : 'text-slate-600'}`} data-id="element-1782" data-mp="wuc2vu424fhz">
              {achievement.name}
            </h4>
            {isUnlocked && isHighlightRarity && <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[9px] font-bold ${config.badgeBg} ${config.badgeText}`} data-id="element-1783" data-mp="15tyuxpawc8yd">
                {config.label}
              </span>}
          </div>

          {!isUnlocked && achievement.progress ? <AchievementProgressBar current={achievement.progress.current} max={achievement.progress.max} label={achievement.description} data-id="element-1784" data-mp="i3v0o8jlllk1" /> : <p className={`text-xs truncate ${isUnlocked ? config.textColor : 'text-slate-500'}`} data-id="element-1785" data-mp="5n3fmh5owtja">
              {achievement.description}
            </p>}
        </div>
      </motion.div>;
  }
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: index * 0.02
  }} className="relative h-full" data-id="element-1786" data-mp="3nrwxun5wrei8">
      <Card className={`
          relative overflow-hidden transition-all duration-300 border-2 h-full flex flex-col p-0
          ${isUnlocked ? `${config.cardBg} ${config.cardBorder} shadow-sm` : 'bg-slate-50 border-slate-200 opacity-70'}
          ${isUnlocked ? 'hover:shadow-md hover:-translate-y-0.5' : ''}
          ${isNewlySeen ? 'animate-shine' : ''}
        `} style={isNewlySeen ? {
      animationIterationCount: 1
    } : undefined} data-id="element-1787" data-mp="3e3f1ci4nvtke">
        {/* Illustration Header (60% of card) */}
        <div className="relative w-full aspect-video bg-white/40 border-b-2 border-white/20" data-id="element-1788" data-mp="uafzssoma2tt">
          <Illustration achievementId={achievement.id} isUnlocked={isUnlocked} rarity={achievement.rarity} variant={achievement.djayVariantUnlocked} size="lg" className="w-full h-full" data-id="element-1789" data-mp="z7y0jvshdbqq" />

          {!isUnlocked && <div className="absolute top-2 right-2 z-30" data-id="element-1790" data-mp="bbk7jmwbjyim">
              <div className="w-6 h-6 rounded-full bg-slate-800/50 flex items-center justify-center backdrop-blur-sm" data-id="element-1791" data-mp="bd50x96wdbew">
                <Lock className="w-3.5 h-3.5 text-white" data-id="element-1792" data-mp="25p316ln8awyn" />
              </div>
            </div>}

          {isUnlocked && isHighlightRarity && <div className="absolute top-2 right-2 z-30" data-id="element-1793" data-mp="2an4tlx6jo8ms">
              <div className={`px-2 py-1 rounded-full text-[10px] font-bold shadow-sm ${config.badgeBg} ${config.badgeText}`} data-id="element-1794" data-mp="2kr1qr7mjuxbi">
                {config.label}
              </div>
            </div>}
        </div>

        {/* Content Footer (40% of card) */}
        <div className="p-3 flex-1 flex flex-col relative z-10" data-id="element-1795" data-mp="2oswt8fg7vgqv">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-1796" data-mp="2orllk4jnhouh">
            <h4 className={`font-black text-sm truncate ${isUnlocked ? 'text-slate-800' : 'text-slate-600'}`} data-id="element-1797" data-mp="33h18tajz3c5f">
              {achievement.name}
            </h4>
          </div>
          <p className={`text-xs leading-snug mb-2 flex-1 ${isUnlocked ? config.textColor : 'text-slate-500'}`} data-id="element-1798" data-mp="3cih9gqkzjzwg">
            {achievement.description}
          </p>

          {!isUnlocked && achievement.progress && <div className="mt-auto pt-2 border-t border-slate-200/50" data-id="element-1799" data-mp="1e3ecjs7996vz">
              <AchievementProgressBar current={achievement.progress.current} max={achievement.progress.max} layout="labelled" data-id="element-1800" data-mp="1e57lxf99657o" />
            </div>}
        </div>
      </Card>
    </motion.div>;
}